// Import the functions you need from the SDKs you need.
//
// Firebase is used here for exactly two things:
//   1. Authentication via getAuth() — for the Google sign-in popup in
//      OAuth.jsx (imports `app` directly).
//   2. Cloud Messaging (FCM) for browser push notifications, behind
//      the SecureContext + ServiceWorker capability check below.
//
// We do NOT use Firestore or Firebase Storage anywhere — the primary
// database is MongoDB Atlas and file storage is DigitalOcean Spaces.
// The previous version imported both getFirestore and getStorage and
// exported `db` and `storage` for "future use," but they had zero
// importers across the app. Dropped to shave the Firebase bundle.
import { initializeApp } from "firebase/app";
import { getMessaging, isSupported as isMessagingSupported } from "firebase/messaging";

// Check if Firebase config is available. Exported so push-notification
// setup can give an honest "not configured" message instead of a
// misleading "try again in a moment" when the deployment is missing its
// Firebase env vars.
export const hasFirebaseConfig = !!(import.meta.env.VITE_API_KEY && import.meta.env.VITE_PROJECT_ID);

const firebaseConfig = hasFirebaseConfig ? {
  apiKey: import.meta.env.VITE_API_KEY,
  authDomain: import.meta.env.VITE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_APP_ID,
  measurementId: import.meta.env.VITE_MEASUREMENT_ID,
} : null;

// Initialize Firebase only if config is available
let app = null;
// Messaging is created lazily inside getMessagingIfSupported() because
// FCM requires a SecureContext (https) AND a Service Worker — neither
// guaranteed on every browser/loopback dev URL. isSupported() probes
// for both before we try to construct the messaging singleton.
let _messaging = null;
let _messagingProbed = false;

if (firebaseConfig) {
  try {
    app = initializeApp(firebaseConfig);
  } catch (error) {
    console.warn('Firebase initialization failed:', error.message);
    console.warn('Google login and push notifications may not work.');
  }
} else {
  console.warn('Firebase configuration not found. Set VITE_API_KEY and VITE_PROJECT_ID in .env');
  console.warn('Google login and push notifications will not work.');
}

/**
 * Returns the Firebase Messaging instance if (a) Firebase is configured,
 * (b) the browser supports the required APIs (Notification, ServiceWorker,
 * PushManager, isSecureContext), and (c) we haven't already failed to set
 * it up. Otherwise returns null and callers should silently skip — push
 * notifications are an enhancement, not a hard requirement.
 *
 * Cached after first call so we don't re-probe support on every render.
 */
export async function getMessagingIfSupported() {
  if (!app) return null;
  if (_messagingProbed) return _messaging;
  _messagingProbed = true;
  try {
    const supported = await isMessagingSupported();
    if (!supported) return null;
    _messaging = getMessaging(app);
    return _messaging;
  } catch (err) {
    // isSupported() throws on some Safari versions when push is partially
    // disabled rather than returning false — treat the throw as "not
    // supported" so the rest of the app keeps working.
    console.warn("[FCM] Messaging not supported on this browser:", err.message);
    return null;
  }
}

export { app };
